import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomFlexLayoutModule } from 'src/app/shared/flex-layout/custom-flex-layout.module';
import { TIMEZONE_ZONES, TimezoneOption } from 'src/app/shared/utils/timezone-zones.constant';
import { SiteVisitService } from 'src/app/services/site-visit.service';
import { ProductService } from 'src/app/services/product.service';
import { CompaniesService } from 'src/app/services/companies.service';
import { RealEstatePropertyService } from 'src/app/services/real-estate-property.service';
import { Constant } from 'src/app/constant/constant';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Subject } from 'rxjs';

@Component({
  selector: 'calsoft-add-site-visit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    CustomFlexLayoutModule
  ],
  templateUrl: './add-site-visit.component.html',
  styleUrls: ['./add-site-visit.component.scss']
})
export class AddSiteVisitComponent implements OnInit, OnChanges, OnDestroy {
  siteVisitForm!: FormGroup;
  minDate: Date | null = null;
  isSaving: boolean = false;
  title: string = 'Add Site Visit';
  zone: TimezoneOption[] = TIMEZONE_ZONES;

  statusList = [
    { viewValue: 'SCHEDULED', value: 'SCHEDULED' },
    { viewValue: 'IN_PROGRESS', value: 'IN_PROGRESS' },
    { viewValue: 'COMPLETED', value: 'COMPLETED' },
    { viewValue: 'CANCELLED', value: 'CANCELLED' }
  ];

  @Input() meetingData: any;
  @Output() onClose = new EventEmitter<any>();
  private _onDestroy = new Subject<void>();
  data: any;

  latitude: number = 0;
  longitude: number = 0;

  // Property chip autocomplete state
  separatorKeysCodes: number[] = [ENTER, COMMA];
  propertyCtrl = new FormControl();
  propertyList: any[] = [];
  selectedProperties: any[] = [];
  @ViewChild('propertyInput') propertyInput!: ElementRef<HTMLInputElement>;
  public page: any = Constant.PAGE;
  public size: any = Constant.SIZE;

  get filteredPropertyList(): any[] {
    if (!this.propertyList) return [];
    if (!this.selectedProperties || this.selectedProperties.length === 0) return this.propertyList;
    return this.propertyList.filter(opt => {
      const optId = opt.entityId || opt.id;
      const optName = (opt.propertyName || opt.name || '').toString().toLowerCase().trim();
      return !this.selectedProperties.some(sp => {
        const spId = sp.entityId || sp.id;
        const spName = (sp.propertyName || sp.name || '').toString().toLowerCase().trim();
        return (optId != null && spId != null && optId == spId) || (optName.length > 0 && spName.length > 0 && optName === spName);
      });
    });
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<AddSiteVisitComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: any,
    private fb: FormBuilder,
    private router: Router,
    private snackBar: MatSnackBar,
    public location: Location,
    private siteVisitService: SiteVisitService,
    private productService: ProductService,
    private companiesService: CompaniesService,
    private realEstatePropertyService: RealEstatePropertyService
  ) {
    this.data = dialogData || {};
  }

  ngOnInit(): void {
    if (this.meetingData) {
      this.data = this.meetingData;
    }

    const defaultTz = 'Asia/Kolkata';

    this.siteVisitForm = this.fb.group({
      title: ['', [Validators.required]],
      description: [''],
      startDateTime: [new Date(), [Validators.required]],
      startTime: ['00:00', [Validators.required]],
      endDateTime: [new Date(), [Validators.required]],
      endTime: ['00:00', [Validators.required]],
      timeZone: [defaultTz],
      visitStatus: ['SCHEDULED', [Validators.required]],
      leadId: [null],
      propertyId: [null],
      assignedAgentId: [null]
    });

    const visitId = this.data?.siteVisitId;
    if (visitId) {
      this.title = 'Edit Site Visit';
      this.siteVisitService.getSiteVisitById(Number(visitId)).subscribe({
        next: (res: any) => {
          if (res && res.data) {
            this.data = { ...this.data, ...res.data };
            this.populateFormData();
          } else {
            this.populateFormData();
          }
        },
        error: (err) => {
          console.error('Error fetching site visit details:', err);
          this.populateFormData();
        }
      });
    } else {
      this.populateFormData();
    }
    this.fetchLocation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['meetingData'] && changes['meetingData'].currentValue) {
      this.data = changes['meetingData'].currentValue;
      const visitId = this.data?.siteVisitId;
      if (visitId && this.siteVisitForm) {
        this.title = 'Edit Site Visit';
        this.siteVisitService.getSiteVisitById(Number(visitId)).subscribe({
          next: (res: any) => {
            if (res && res.data) {
              this.data = { ...this.data, ...res.data };
              this.populateFormData();
            } else {
              this.populateFormData();
            }
          },
          error: () => this.populateFormData()
        });
      } else if (this.siteVisitForm) {
        this.populateFormData();
      }
    }
  }

  populateFormData(): void {
    if (!this.siteVisitForm) return;

    const titleVal = this.data?.title || this.data?.summary || this.data?.taskDescription || '';
    const descVal = this.data?.description || this.data?.notes || '';
    const leadIdVal = this.data?.leadId || this.data?.entityId || this.data?.lead_id || null;
    const salesPersonIdVal = this.data?.salesPersonId || this.data?.sales_personId || this.data?.assignedAgentId || localStorage.getItem('ID') || localStorage.getItem('userId') || null;

    const startVal = this.data?.visitStartTime || this.data?.startDateTime || this.data?.start_datetime || this.data?.startTime || this.data?.start?.dateTime || this.data?.start;
    const endVal = this.data?.visitEndTime || this.data?.endDateTime || this.data?.end_datetime || this.data?.endTime || this.data?.end?.dateTime || this.data?.end;

    let startDate = new Date();
    let startTimeStr = this.formatTimeFromDate(startDate);
    if (startVal) {
      const d = new Date(startVal);
      if (!isNaN(d.getTime())) {
        startDate = d;
        startTimeStr = this.formatTimeFromDate(d);
      }
    }

    let endDate = new Date(startDate.getTime() + 30 * 60000);
    let endTimeStr = this.formatTimeFromDate(endDate);
    if (endVal) {
      const d = new Date(endVal);
      if (!isNaN(d.getTime())) {
        endDate = d;
        endTimeStr = this.formatTimeFromDate(d);
      }
    }

    const propName = this.data?.propertyName || this.data?.PropertyName || this.data?.property || null;
    const propId = this.data?.propertyId || this.data?.PropertyId || null;

    this.siteVisitForm.patchValue({
      title: titleVal,
      description: descVal,
      startDateTime: startDate,
      startTime: startTimeStr,
      endDateTime: endDate,
      endTime: endTimeStr,
      visitStatus: this.data?.visitStatus || 'SCHEDULED',
      leadId: leadIdVal ? (typeof leadIdVal === 'number' ? leadIdVal : Number(leadIdVal) || leadIdVal) : null,
      propertyId: propId ? (typeof propId === 'number' ? propId : Number(propId) || propId) : null,
      assignedAgentId: salesPersonIdVal ? (typeof salesPersonIdVal === 'number' ? salesPersonIdVal : Number(salesPersonIdVal) || salesPersonIdVal) : null
    });

    // Pre-fill selected property if passed via data
    if (propName || propId) {
      this.selectedProperties = [{ entityId: propId, id: propId, name: propName || `Property #${propId}`, propertyName: propName || `Property #${propId}` }];
      if (propId && !propName) {
        this.fetchPropertyNameById(propId);
      }
    }
  }

  fetchPropertyNameById(propId: any): void {
    const numId = Number(propId);
    if (!numId) return;
    this.realEstatePropertyService.getPropertyById(numId).subscribe({
      next: (res: any) => {
        const foundName = res?.propertyName || res?.data?.propertyName || res?.name;
        if (foundName) {
          this.selectedProperties = [{ entityId: propId, id: propId, name: foundName, propertyName: foundName }];
        }
      },
      error: () => { }
    });
  }

  private formatTimeFromDate(d: Date): string {
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // ─── Property autocomplete methods ────────────────────────────────────────

  onPropertyInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const raw = inputElement.value || '';
    const noLeading = raw.replace(/^\s+/, '');
    if (noLeading !== raw) { inputElement.value = noLeading; }
    const inputValue = noLeading.trim();
    if (inputValue.length > 0) {
      this.getPropertyData(inputValue);
    }
  }

  getPropertyData(value: string): void {
    const sortParam = 'updatedDate,' + Constant.Dsc;
    const searchTerm = "propertyName:'" + '*' + value + '*' + "'  OR status:'" + '*' + 1 + ',*' + "' ";
    this.realEstatePropertyService.searchProperties(searchTerm, this.page, this.size, sortParam).subscribe({
      next: (res: any) => { this.propertyList = res['content'] || []; },
      error: () => { }
    });
  }

  onPropertyOptionSelected(option: any): void {
    if (!option || typeof option === 'string') return;
    const propId = option.entityId || option.id;
    const propName = option.propertyName || option.name;
    if (propName || propId) {
      this.selectedProperties = [{ entityId: propId, id: propId, name: propName, propertyName: propName }];
    }
    const primaryId = this.selectedProperties.length > 0 ? (this.selectedProperties[0].entityId || this.selectedProperties[0].id) : null;
    this.siteVisitForm.patchValue({ propertyId: primaryId });
    this.propertyCtrl.setValue('');
    if (this.propertyInput) { this.propertyInput.nativeElement.value = ''; }
  }

  removeProperty(item: any): void {
    const index = this.selectedProperties.indexOf(item);
    if (index >= 0) { this.selectedProperties.splice(index, 1); }
    const nextId = this.selectedProperties.length > 0 ? (this.selectedProperties[0].entityId || this.selectedProperties[0].id) : null;
    this.siteVisitForm.patchValue({ propertyId: nextId });
  }

  fetchLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.latitude = pos.coords.latitude;
          this.longitude = pos.coords.longitude;
        },
        (err) => {
          console.warn('Geolocation fetch error:', err);
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }
  }

  async resolveCurrentLocation(): Promise<{ lat: number | null; lng: number | null }> {
    if (this.latitude && this.longitude) {
      return { lat: this.latitude, lng: this.longitude };
    }
    const savedLat = localStorage.getItem('lastTrackingLatitude');
    const savedLng = localStorage.getItem('lastTrackingLongitude');
    if (savedLat && savedLng && Number(savedLat) !== 0 && Number(savedLng) !== 0) {
      this.latitude = Number(savedLat);
      this.longitude = Number(savedLng);
      return { lat: this.latitude, lng: this.longitude };
    }
    try {
      if (navigator.geolocation) {
        const pos = await new Promise<any>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 4000 });
        });
        if (pos && pos.coords) {
          this.latitude = pos.coords.latitude;
          this.longitude = pos.coords.longitude;
          return { lat: this.latitude, lng: this.longitude };
        }
      }
    } catch (e) {
      console.warn('Geolocation fetch fallback failed:', e);
    }
    return { lat: this.latitude || null, lng: this.longitude || null };
  }

  getCombinedDateTime(dateVal: any, timeStr: string): Date {
    const d = new Date(dateVal);
    if (timeStr) {
      const [h, m] = timeStr.split(':').map(Number);
      d.setHours(h, m, 0, 0);
    }
    return d;
  }

  async saveSiteVisit() {
    if (this.isSaving) return;

    if (this.siteVisitForm.invalid) {
      this.siteVisitForm.markAllAsTouched();
      this.snackBar.open('Please fill all required fields correctly (Title, Start/End Time).', '×', {
        panelClass: ['error'],
        verticalPosition: 'bottom',
        duration: 3000
      });
      return;
    }

    this.isSaving = true;
    const formVal = this.siteVisitForm.value;

    const loc = await this.resolveCurrentLocation();
    const startDt = this.getCombinedDateTime(formVal.startDateTime, formVal.startTime);
    const endDt = this.getCombinedDateTime(formVal.endDateTime, formVal.endTime);

    const primaryLeadId = formVal.leadId || this.data?.leadId || this.data?.entityId || this.data?.lead_id || null;
    let primaryPropertyId = formVal.propertyId ||
      (this.selectedProperties.length > 0 ? (this.selectedProperties[0].entityId || this.selectedProperties[0].id || this.selectedProperties[0].propertyId) : null);

    if (!primaryPropertyId && this.propertyCtrl.value) {
      const typed = String(this.propertyCtrl.value).trim().toLowerCase();
      const match = (this.propertyList || []).find(p => (p.propertyName || p.name || '').toLowerCase() === typed);
      if (match) {
        primaryPropertyId = match.entityId || match.id || match.propertyId || match.entity_id;
      }
    }

    const trackingId = localStorage.getItem('authUserId') || localStorage.getItem('userId') || localStorage.getItem('ID');

    const payload: any = {
      title: formVal.title,
      description: formVal.description,
      visitStartTime: startDt,
      visitEndTime: endDt,
      visitStatus: formVal.visitStatus || 'SCHEDULED',
      leadId: primaryLeadId ? String(primaryLeadId) : null,
      propertyId: primaryPropertyId ? String(primaryPropertyId) : null,
      assignedAgentId: formVal.assignedAgentId ? Number(formVal.assignedAgentId) : null,
      salesPersonTrackingId: trackingId ? Number(trackingId) : null,
      checkinLatitude: loc.lat,
      checkinLongitude: loc.lng
    };

    const visitId = this.data?.siteVisitId;
    if (visitId) {
      payload.siteVisitId = Number(visitId);
    }

    console.log('[AddSiteVisit] Saving site visit with payload:', payload);

    this.siteVisitService.createSiteVisit(payload).subscribe({
      next: async (res: any) => {
        const isUpdate = !!visitId;
        const msg = isUpdate ? 'Site Visit updated successfully!' : 'Site Visit created successfully!';
        this.snackBar.open(res?.status_message || msg, '×', {
          panelClass: ['success'],
          verticalPosition: 'bottom',
          duration: 3000
        });

        const createdId = res?.id || res?.data?.id || res?.entityId || res?.data?.entityId || res?.siteVisitId;
        if (!isUpdate && createdId && loc.lat != null && loc.lng != null) {
          this.siteVisitService.checkInSiteVisit(createdId, loc.lat, loc.lng).subscribe({
            next: (checkInRes: any) => console.log('Check-in successful:', checkInRes),
            error: (checkInErr: any) => console.error('Check-in error:', checkInErr)
          });
        }
        this.isSaving = false;
        this.close();
      },
      error: (err: any) => {
        this.isSaving = false;
        console.error('Error saving site visit:', err);
        this.snackBar.open(err?.error?.status_message || 'Failed to save site visit', '×', {
          panelClass: ['error'],
          verticalPosition: 'bottom',
          duration: 3000
        });
      }
    });
  }


  close() {
    const eventType = this.data?.siteVisitId ? 'update' : 'add';
    if (this.dialogRef) {
      this.dialogRef.close({ event: eventType });
    }
    this.onClose.emit({ event: eventType });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
